/**
 * Canvas painter.
 *
 * It is handed { pixelRatio, zoom, scrollX, scrollY } explicitly and never reads
 * window.devicePixelRatio. That is what makes PNG export the same renderer at
 * pixelRatio 2 and zoom 1 rather than a second code path.
 */

import { fontShorthand, type MeasureText, type Op, type PathCmd } from '../geometry/drawable';
import { drawableOf } from '../geometry/cache';
import { rectsIntersect, worldBounds, type Rect } from '../geometry/transform';
import type { SlateElement } from '../scene/types';

export interface SceneRenderOptions {
  /** CSS pixels. */
  readonly width: number;
  readonly height: number;
  readonly pixelRatio: number;
  readonly zoom: number;
  readonly scrollX: number;
  readonly scrollY: number;
  readonly background: string | null;
}

export function buildPath(cmds: readonly PathCmd[]): Path2D {
  const path = new Path2D();
  for (const cmd of cmds) {
    switch (cmd.c) {
      case 'M':
        path.moveTo(cmd.p[0], cmd.p[1]);
        break;
      case 'L':
        path.lineTo(cmd.p[0], cmd.p[1]);
        break;
      case 'C':
        path.bezierCurveTo(cmd.c1[0], cmd.c1[1], cmd.c2[0], cmd.c2[1], cmd.p[0], cmd.p[1]);
        break;
      case 'Z':
        path.closePath();
        break;
    }
  }
  return path;
}

function paintOp(ctx: CanvasRenderingContext2D, op: Op): void {
  if (op.k === 'path') {
    const path = buildPath(op.cmds);
    if (op.fill) {
      ctx.fillStyle = op.fill.color;
      ctx.fill(path);
    }
    if (op.stroke && op.stroke.width > 0) {
      ctx.strokeStyle = op.stroke.color;
      ctx.lineWidth = op.stroke.width;
      ctx.lineCap = op.stroke.cap;
      ctx.lineJoin = op.stroke.join;
      ctx.setLineDash(op.stroke.dash ? [...op.stroke.dash] : []);
      ctx.stroke(path);
      ctx.setLineDash([]);
    }
    return;
  }
  // Text: left-anchored with an alphabetic baseline, matching the SVG side
  // exactly. textBaseline 'top' is font dependent and has no SVG equivalent.
  ctx.font = fontShorthand(op.font);
  ctx.fillStyle = op.color;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  for (const line of op.lines) {
    if (line.text.length > 0) ctx.fillText(line.text, line.x, line.y);
  }
}

export function paintElement(
  ctx: CanvasRenderingContext2D,
  el: SlateElement,
  measure: MeasureText,
): void {
  const drawable = drawableOf(el, measure);
  ctx.save();
  // The canonical transform. Its SVG twin is translate(x y) rotate(deg w/2 h/2).
  ctx.translate(el.x + el.width / 2, el.y + el.height / 2);
  if (el.angle !== 0) ctx.rotate(el.angle);
  ctx.translate(-el.width / 2, -el.height / 2);
  ctx.globalAlpha = drawable.opacity;
  for (const op of drawable.ops) paintOp(ctx, op);
  ctx.restore();
}

export function viewportWorldRect(opts: SceneRenderOptions): Rect {
  return {
    x: opts.scrollX,
    y: opts.scrollY,
    width: opts.width / opts.zoom,
    height: opts.height / opts.zoom,
  };
}

export function renderScene(
  ctx: CanvasRenderingContext2D,
  elements: readonly SlateElement[],
  opts: SceneRenderOptions,
  measure: MeasureText,
  /** Chrome painted under the scene, in screen space. Never used by exports. */
  paintUnderlay?: (ctx: CanvasRenderingContext2D) => void,
): void {
  const { pixelRatio, zoom } = opts;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, opts.width * pixelRatio, opts.height * pixelRatio);
  if (opts.background) {
    ctx.fillStyle = opts.background;
    ctx.fillRect(0, 0, opts.width * pixelRatio, opts.height * pixelRatio);
  }
  if (paintUnderlay) {
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    paintUnderlay(ctx);
  }

  const s = pixelRatio * zoom;
  ctx.setTransform(s, 0, 0, s, -opts.scrollX * s, -opts.scrollY * s);

  const view = viewportWorldRect(opts);
  for (const el of elements) {
    if (el.isDeleted) continue;
    if (el.width === 0 && el.height === 0 && el.type !== 'text') continue;
    // Culling on the rotation-expanded world box.
    if (!rectsIntersect(worldBounds(el), view)) continue;
    paintElement(ctx, el, measure);
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}
